/**
 * 仿真看板（Simulation Dashboard）—— 执行人排行榜。
 *
 * 原页面的「全平台累计 KPI + 运行趋势」已迁入「概览」landing 页（对所有登录用户
 * 公开）。本页只保留**执行人排行榜**，仍受独立权限点 menu:sim-dashboard 保护
 * （菜单 / 路由 RequirePermission / 后端 /ranking 端点 require_permission 三层同码
 * 硬门），并归入侧栏「系统」分组。
 *
 * 风格对齐「概览」（Linear 亮色）：任务数靛蓝 #5e6ad2、里程翠绿 #10b981。
 * 数据源为非分区汇总表 job_stat（毫秒级）。里程后端存米，前端展示 ÷1000 转 km。
 */
import { useCallback, useEffect, useState } from 'react';
import { Button, Segmented, Select, Table, Tooltip, App } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  getDashboardRanking,
  type DashboardRankingRow,
  type RankingSimType,
  type RankingTimeRange,
} from '@/apis/simDashboard';
import './index.less';

/** 与 Overview 一致的双指标主色。 */
const COLOR_TASK = '#5e6ad2'; // 任务数 = PNC 靛蓝
const COLOR_MILEAGE = '#10b981'; // 里程 = 感知 翠绿

/** 米 → 公里，保留 1 位小数。 */
const toKm = (meters: number): number => Math.round((meters / 1000) * 10) / 10;

/** 千分位整数。 */
const fmtInt = (n: number): string => n.toLocaleString('en-US');

/** 榜单条数：全部（0，分页展示）/ Top N（不分页）。默认全部。 */
const LIMIT_OPTIONS = [
  { label: '全部', value: 0 },
  ...[10, 20, 50].map((n) => ({ label: `Top ${n}`, value: n })),
];

/** 仿真类型过滤：全部 / PNC / 感知（区分 planning / perception）。 */
const SIM_TYPE_OPTIONS: { label: string; value: RankingSimType }[] = [
  { label: '全部', value: 'all' },
  { label: 'PNC', value: 'planning' },
  { label: '感知', value: 'perception' },
];

/** 统计时间维度：默认最近 7 天，另含 今日 / 本周 / 本月 / 历史累计。 */
const TIME_RANGE_OPTIONS: { label: string; value: RankingTimeRange }[] = [
  { label: '今日', value: 'day' },
  { label: '最近 7 天', value: '7d' },
  { label: '本周', value: 'week' },
  { label: '本月', value: 'month' },
  { label: '历史累计', value: 'all' },
];

/** 当前时间维度的文案（用于榜单副标题）。 */
const TIME_RANGE_LABEL: Record<RankingTimeRange, string> = {
  day: '今日',
  '7d': '最近 7 天',
  week: '本周',
  month: '本月',
  all: '历史累计',
};

export default function SimDashboardPage() {
  const { message } = App.useApp();
  const [refreshToken, setRefreshToken] = useState(0);
  const [asOf, setAsOf] = useState<Date | null>(null);

  const [limit, setLimit] = useState(0);
  const [simType, setSimType] = useState<RankingSimType>('all');
  const [timeRange, setTimeRange] = useState<RankingTimeRange>('7d');
  const [taskRank, setTaskRank] = useState<DashboardRankingRow[]>([]);
  const [mileageRank, setMileageRank] = useState<DashboardRankingRow[]>([]);
  const [rankLoading, setRankLoading] = useState(false);

  const handleRefresh = useCallback(() => setRefreshToken((v) => v + 1), []);

  // 双榜（随仿真类型 / 时间维度 / 条数 / 刷新变化重拉）
  useEffect(() => {
    let alive = true;
    setRankLoading(true);
    Promise.all([
      getDashboardRanking('task', limit, simType, timeRange),
      getDashboardRanking('mileage', limit, simType, timeRange),
    ])
      .then(([t, m]) => {
        if (!alive) return;
        setTaskRank(t.rows);
        setMileageRank(m.rows);
        setAsOf(t.as_of ? new Date(t.as_of) : new Date());
      })
      .catch(() => message.error('加载排行榜失败'))
      .finally(() => {
        if (alive) setRankLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [limit, simType, timeRange, refreshToken, message]);

  const rankColumns = useCallback(
    (metric: 'task' | 'mileage'): ColumnsType<DashboardRankingRow> => [
      {
        title: '',
        dataIndex: 'rank',
        width: 52,
        render: (rank: number) => (
          <span
            className={
              'sim-dashboard__rank-idx' +
              (rank <= 3
                ? ` sim-dashboard__rank-idx--${metric === 'task' ? 'task' : 'mileage'}`
                : '')
            }
          >
            {rank}
          </span>
        ),
      },
      {
        title: '执行人',
        dataIndex: 'creator_name',
        ellipsis: true,
        // 展示中文名（后端按 email 解析 sys_user.name）；命不到回退 creator（email/系统账号）。
        // hover 显示 creator（email/身份锚），便于重名区分与定位。
        render: (_: string, row: DashboardRankingRow) => (
          <Tooltip title={row.creator} placement="top">
            <span>{row.creator_name || row.creator || '未知'}</span>
          </Tooltip>
        ),
      },
      {
        title: metric === 'task' ? '任务数' : '里程 (km)',
        dataIndex: metric === 'task' ? 'task_num' : 'mileage',
        align: 'right',
        width: 120,
        render: (_: number, row: DashboardRankingRow) => (
          <span className="sim-dashboard__rank-num">
            {metric === 'task' ? fmtInt(row.task_num) : fmtInt(toKm(row.mileage))}
          </span>
        ),
      },
      {
        title: metric === 'task' ? '里程 (km)' : '任务数',
        dataIndex: metric === 'task' ? 'mileage' : 'task_num',
        align: 'right',
        width: 110,
        render: (_: number, row: DashboardRankingRow) => (
          <span className="sim-dashboard__rank-sub">
            {metric === 'task' ? fmtInt(toKm(row.mileage)) : fmtInt(row.task_num)}
          </span>
        ),
      },
    ],
    [],
  );

  // 「全部」(limit=0) 时分页展示（每页 10）；Top N 时不分页、直出整段榜单。
  const rankPagination =
    limit === 0
      ? {
          pageSize: 10,
          size: 'small' as const,
          showSizeChanger: false,
          hideOnSinglePage: true,
        }
      : (false as const);

  return (
    <div className="sim-dashboard">
      {/* 头部 */}
      <div className="sim-dashboard__header">
        <div>
          <h1 className="sim-dashboard__title">执行人排行榜</h1>
          <p className="sim-dashboard__subtitle">
            全平台运营数据 · PNC + 感知
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {asOf && (
            <span className="sim-dashboard__asof">
              截至 {asOf.toLocaleString('zh-CN', { hour12: false })}
            </span>
          )}
          <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
            刷新
          </Button>
        </div>
      </div>

      {/* 排行榜双栏 */}
      <div className="sim-dashboard__card">
        <div className="sim-dashboard__toolbar">
          <div className="sim-dashboard__toolbar-group">
            <span className="sim-dashboard__toolbar-label">仿真类型</span>
            <Segmented
              options={SIM_TYPE_OPTIONS}
              value={simType}
              onChange={(v) => setSimType(v as RankingSimType)}
            />
          </div>
          <div className="sim-dashboard__toolbar-group">
            <span className="sim-dashboard__toolbar-label">统计维度</span>
            <Segmented
              options={TIME_RANGE_OPTIONS}
              value={timeRange}
              onChange={(v) => setTimeRange(v as RankingTimeRange)}
            />
          </div>
          <div className="sim-dashboard__toolbar-spacer" />
          <Select
            options={LIMIT_OPTIONS}
            value={limit}
            onChange={setLimit}
            style={{ width: 120 }}
          />
        </div>
        <p className="sim-dashboard__card-note">
          按执行人聚合 · {SIM_TYPE_OPTIONS.find((o) => o.value === simType)?.label} ·{' '}
          {TIME_RANGE_LABEL[timeRange]}
        </p>
        <div className="sim-dashboard__rankings">
          <div>
            <h3 className="sim-dashboard__rank-title">
              <span
                className="sim-dashboard__rank-dot"
                style={{ background: COLOR_TASK }}
              />
              任务数榜
            </h3>
            <Table
              className="sim-dashboard__rank-table"
              rowKey="rank"
              size="middle"
              loading={rankLoading}
              columns={rankColumns('task')}
              dataSource={taskRank}
              pagination={rankPagination}
            />
          </div>
          <div>
            <h3 className="sim-dashboard__rank-title">
              <span
                className="sim-dashboard__rank-dot"
                style={{ background: COLOR_MILEAGE }}
              />
              里程榜
            </h3>
            <Table
              className="sim-dashboard__rank-table"
              rowKey="rank"
              size="middle"
              loading={rankLoading}
              columns={rankColumns('mileage')}
              dataSource={mileageRank}
              pagination={rankPagination}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
